import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  AbstractButton,
  Box,
  Card,
  Flex,
  Icon,
  IconButton,
  IconProps,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
} from 'pounceV2';
import { useTheme } from '@mui/material';
import { Permission } from 'Generated/schema';
import useCheckPermissions from 'Hooks/useCheckPermissions';
import useFeatureToggle from 'Hooks/useFeatureToggle';
import urls from 'Source/urls';

export interface AddComposerMenuV3Props {
  disabled?: boolean;
}

interface MenuRowProps {
  icon: IconProps['type'];
  label: string;
  description: string;
  testId: string;
  onClick: () => void;
}

const MenuRow: React.FC<MenuRowProps> = ({ icon, label, description, testId, onClick }) => {
  const {
    palette: { tokens, componentTokens },
  } = useTheme();
  const hoverBg = componentTokens.selectMenuItems.color.item.background.row.hover;
  return (
    <AbstractButton
      type="button"
      onClick={onClick}
      display="flex"
      alignItems="center"
      gap={3}
      width="100%"
      textAlign="left"
      px={3}
      py={2.5}
      borderRadius="medium"
      cursor="pointer"
      transition="background-color 150ms ease"
      sx={{
        '&:hover': { backgroundColor: hoverBg },
        '&:focus-visible': { backgroundColor: hoverBg },
      }}
      data-testid={testId}
    >
      <Flex flexShrink={0} alignItems="center" justifyContent="center">
        <Icon type={icon} size="small" color={tokens.content.text.neutral.base} />
      </Flex>
      <Flex direction="column" gap={0.5} flex={1} minWidth={0}>
        <Text fontSize="medium" fontWeight="medium" lineHeight="normal">
          {label}
        </Text>
        <Text fontSize="medium" color={tokens.content.text.neutral[3]} lineHeight="relaxed">
          {description}
        </Text>
      </Flex>
    </AbstractButton>
  );
};

const AddComposerMenuV3: React.FC<AddComposerMenuV3Props> = ({ disabled = false }) => {
  const {
    palette: { componentTokens, tokens },
  } = useTheme();
  const history = useHistory();

  const canReadMcp = useCheckPermissions(Permission.McpServerRead);
  const canViewSkills = useCheckPermissions(Permission.ManageAiSkills);
  const mcpEnabled = useFeatureToggle('ENABLE_AI_MCP_SERVERS');

  const showMcp = mcpEnabled && canReadMcp;
  const showSkill = canViewSkills;

  if (!showMcp && !showSkill) {
    return (
      <Tooltip content="No add actions available for your role">
        <Box as="span">
          <IconButton
            icon="add"
            aria-label="Add skills or MCP servers"
            size="medium"
            variant="ghost"
            disabled
          />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Popover>
      {({ close }) => (
        <>
          <Tooltip content="Add skills or MCP servers">
            <Box as="span">
              <PopoverTrigger
                as={IconButton}
                icon="add"
                aria-label="Add skills or MCP servers"
                size="medium"
                variant="ghost"
                disabled={disabled}
                data-testid="add-composer-menu-trigger"
              />
            </Box>
          </Tooltip>
          <PopoverContent alignment="top-left" zIndex={1400}>
            <Card
              minWidth={280}
              mb={1}
              p={1}
              border={`1px solid ${tokens.stroke.neutral[5]}`}
              backgroundColor={componentTokens.selectMenuItems.color.item.background.containerBase}
            >
              <Flex direction="column" gap={0}>
                {showMcp && (
                  <MenuRow
                    icon="integrations"
                    label="Manage MCP servers"
                    description="Connect Slack, GitHub, Notion, and more in Settings."
                    testId="add-composer-menu-mcp"
                    onClick={() => {
                      history.push(urls.settings.mcp.list());
                      close();
                    }}
                  />
                )}
                {showSkill && (
                  <MenuRow
                    icon="sparkle"
                    label="Add skill"
                    description="Author a reusable AI skill in Settings."
                    testId="add-composer-menu-skill"
                    onClick={() => {
                      history.push(urls.settings.aiSkills.list());
                      close();
                    }}
                  />
                )}
              </Flex>
            </Card>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
};

export default React.memo(AddComposerMenuV3);
